/**
 * JS-1082: fetch + resolve the idea context a ticker-view deep link carries.
 *
 * Lives in `domains/insights/lib` (not `domains/trade-ideas/lib`) because
 * `TickerDashboard.tsx` itself lives in `domains/insights` and calls
 * `apiRequest` directly (no `ApiFn` dependency-injection seam, unlike the
 * trade-ideas domain's hooks) -- this module matches that existing calling
 * convention rather than introducing a second one. The RESPONSE SHAPE is
 * still owned by `domains/trade-ideas/lib/contracts.ts`
 * (`ideaContextResponseSchema` / `decodeIdeaContext`), reused verbatim here,
 * never redeclared.
 */

import { apiRequest } from "@/shared/lib/apiClient";
import {
  decodeIdeaContext,
  type IdeaContextResponse,
} from "@/domains/trade-ideas/lib/contracts";

/**
 * Fetch and tolerantly decode one idea's context by its ledger id.
 *
 * Tolerant top-level: ANY failure (network error, non-2xx status, malformed
 * body) resolves to `null` rather than rejecting -- the caller (the ticker
 * dashboard's context-resolution effect) must degrade to a context-free
 * render, never a blank page or a thrown error. This is the ticket's
 * explicit "unresolvable/invalid/absent -> context-free render" contract.
 */
export async function fetchIdeaContext(
  ledgerId: string,
  options: { signal?: AbortSignal } = {},
): Promise<IdeaContextResponse | null> {
  try {
    const raw = await apiRequest<unknown>(
      "GET",
      `/api/v1/trade-ideas/screener/idea-context/${encodeURIComponent(ledgerId)}`,
      { signal: options.signal },
    );
    return decodeIdeaContext(raw);
  } catch {
    // A 404 (ledger-only-row absent), a 400 (malformed id we somehow still
    // tried), a 503 (screener reads disabled), or any network failure are
    // ALL the same case from this deep link's point of view: context could
    // not be resolved right now, so render context-free. The ticket is
    // explicit that this must never surface as a thrown error or a blank
    // page.
    return null;
  }
}
